// Indicador visual del progreso de un envío a través de sus etapas:
//   1) Solicitado  → la secretaria creó la solicitud
//   2) Aprobado    → el admin la aprobó (definió cantidad)
//   3) Despachado  → se agregó la guía de despacho
//
// Si el envío fue rechazado, se muestra un estado especial en rojo.
//
// Props:
//   - estado: 'pendiente' | 'aprobado' | 'rechazado'   (campo resultado del envío)
//   - tieneGuia: boolean                                (si ya tiene guía de despacho)
//   - compacto: boolean (opcional)                      (versión más pequeña para tablas)

export default function EnvioEstadoStepper({ estado, tieneGuia = false, compacto = false }) {
  // Caso especial: rechazado
  if (estado === 'rechazado') {
    return (
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-50 border border-red-200">
        <span className="text-red-600 text-lg leading-none">✕</span>
        <span className="text-sm font-semibold text-red-700">Rechazado</span>
      </div>
    );
  }

  // Determinar el paso actual (1, 2 o 3)
  // pendiente = paso 1 cumplido, esperando aprobación
  // aprobado sin guía = paso 2 cumplido, esperando guía
  // aprobado con guía = paso 3 cumplido (completo)
  let pasoActual;
  if (estado === 'aprobado' && tieneGuia) {
    pasoActual = 3;
  } else if (estado === 'aprobado') {
    pasoActual = 2;
  } else {
    pasoActual = 1;
  }

  const pasos = [
    { numero: 1, label: 'Solicitado', icono: '📝' },
    { numero: 2, label: 'Aprobado', icono: '✅' },
    { numero: 3, label: 'Despachado', icono: '🚚' },
  ];

  const tamCirculo = compacto ? 'w-6 h-6 text-xs' : 'w-8 h-8 text-sm';
  const tamLabel = compacto ? 'text-[10px]' : 'text-xs';
  const tamLinea = compacto ? 'w-4' : 'w-8';

  return (
    <div className="inline-flex items-center">
      {pasos.map((paso, idx) => {
        const completado = paso.numero < pasoActual;
        const actual = paso.numero === pasoActual;
        const pendiente = paso.numero > pasoActual;

        // Colores según el estado del paso
        let circuloClases;
        if (completado) {
          circuloClases = 'bg-green-500 text-white border-green-500';
        } else if (actual) {
          // El paso actual: si es el 3 y está completo, verde; si está "esperando", ámbar
          circuloClases = pasoActual === 3
            ? 'bg-green-500 text-white border-green-500'
            : 'bg-amber-400 text-white border-amber-400 ring-2 ring-amber-200';
        } else {
          circuloClases = 'bg-gray-100 text-gray-400 border-gray-300';
        }

        return (
          <div key={paso.numero} className="flex items-center">
            <div className="flex flex-col items-center">
              <div className={`flex items-center justify-center rounded-full border-2 font-bold ${tamCirculo} ${circuloClases}`}>
                {completado ? '✓' : paso.numero}
              </div>
              <span className={`mt-1 font-medium ${tamLabel} ${
                actual ? 'text-gray-800' : completado ? 'text-green-700' : 'text-gray-400'
              }`}>
                {paso.label}
              </span>
            </div>
            {/* Línea conectora entre pasos */}
            {idx < pasos.length - 1 && (
              <div className={`h-0.5 ${tamLinea} mx-1 mb-4 ${
                paso.numero < pasoActual ? 'bg-green-500' : 'bg-gray-300'
              }`} />
            )}
          </div>
        );
      })}
    </div>
  );
}
